import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CaretLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/roles";
import { createIncident } from "@/lib/incidents";
import { notifyIncident } from "@/lib/notifications";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import {
  listChildrenInClassroom,
  listTeacherClassrooms,
} from "@/lib/dailyReports";
import {
  INCIDENT_TYPES,
  INCIDENT_TYPE_META,
  NOTIFICATION_METHODS,
  NOTIFICATION_METHOD_LABELS,
  SEVERITIES,
  SEVERITY_META,
  toLocalDateTimeInput,
} from "@/lib/incidentTypes";

export default function TeacherIncidentNew() {
  const { profile, role } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const preselectedChildId = search.get("childId");

  const isAdmin = role === ROLES.ADMIN;
  const basePath = isAdmin ? "/admin/incidents" : "/teacher/incidents";

  const [classrooms, setClassrooms] = useState([]);
  const [children, setChildren] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    classroomId: "",
    childId: preselectedChildId || "",
    incidentType: "injury",
    severity: "low",
    occurredAt: toLocalDateTimeInput(new Date()),
    location: "",
    description: "",
    actionTaken: "",
    parentNotified: false,
    parentNotificationMethod: "message",
  });

  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      let rooms = [];
      if (isAdmin && isSupabaseConfigured) {
        const { data } = await supabase
          .from("classrooms")
          .select("id, name")
          .order("name");
        rooms = data || [];
      } else {
        rooms = await listTeacherClassrooms(profile.id);
      }
      setClassrooms(rooms);
      const firstRoom = rooms[0];
      if (firstRoom) {
        setForm((f) => ({ ...f, classroomId: f.classroomId || firstRoom.id }));
      }
    })();
  }, [profile?.id, isAdmin]);

  useEffect(() => {
    if (!form.classroomId) return;
    (async () => {
      const kids = await listChildrenInClassroom(form.classroomId);
      setChildren(kids);
      setForm((f) => ({
        ...f,
        childId:
          f.childId && kids.some((k) => k.id === f.childId)
            ? f.childId
            : kids[0]?.id || "",
      }));
    })();
  }, [form.classroomId]);

  const descErr = form.description.trim().length < 10
    ? "Description must be at least 10 characters."
    : null;
  const actionErr = form.actionTaken.trim().length < 3
    ? "Action taken is required."
    : null;

  const canSubmit = useMemo(
    () =>
      form.classroomId &&
      form.childId &&
      form.incidentType &&
      form.severity &&
      form.occurredAt &&
      !descErr &&
      !actionErr,
    [form, descErr, actionErr],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const occurredAtISO = new Date(form.occurredAt).toISOString();
      const row = await createIncident({
        organizationId: profile.organization_id,
        childId: form.childId,
        classroomId: form.classroomId,
        incidentType: form.incidentType,
        severity: form.severity,
        occurredAt: occurredAtISO,
        location: form.location.trim() || null,
        description: form.description.trim(),
        actionTaken: form.actionTaken.trim(),
        parentNotifiedAt: form.parentNotified ? new Date().toISOString() : null,
        parentNotificationMethod: form.parentNotified
          ? form.parentNotificationMethod
          : null,
        reportedBy: profile.id,
      });
      toast.success("Incident report submitted");

      // Fire-and-forget parent notification. We look up the child's
      // primary contact via a lightweight select so we don't pass a
      // half-populated payload.
      if (isSupabaseConfigured) {
        try {
          const { data: child } = await supabase
            .from("children")
            .select(`
              id, first_name, last_name,
              family:families(id, primary_contact_profile_id,
                primary:profiles!families_primary_contact_profile_id_fkey(id, email)),
              organization:organizations(id, display_name, name)
            `)
            .eq("id", form.childId)
            .maybeSingle();
          const parentEmail = child?.family?.primary?.email;
          const parentProfileId = child?.family?.primary_contact_profile_id;
          if (parentEmail && parentProfileId) {
            notifyIncident({
              incident_id: row.id,
              child_id: child.id,
              child_first_name: child.first_name,
              child_last_name: child.last_name,
              occurred_at: occurredAtISO,
              severity: form.severity,
              summary: form.description.trim(),
              parent_email: parentEmail,
              parent_profile_id: parentProfileId,
              organization_id: profile.organization_id,
              organization_name: child.organization?.display_name || child.organization?.name || null,
            });
          }
        } catch {
          // Non-blocking — log panel owns the delivery truth.
        }
      }

      navigate(`${basePath}/${row.id}`);
    } catch (err) {
      toast.error(err.message || "Could not submit incident");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={isAdmin ? "admin-incident-new" : "teacher-incident-new"} className="space-y-5">
      <Link
        to={basePath}
        data-testid="incident-new-back"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
      >
        <CaretLeft size={16} weight="bold" />
        Back to incidents
      </Link>

      <header>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          New incident report
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900">
          Report an incident
        </h1>
        <p className="mt-2 text-sm text-neutral-600">
          Please complete every field as clearly as possible. Submitted reports
          are visible to the family and to admin; they cannot be deleted.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="space-y-5" data-testid="incident-form">
        {/* Classroom — admins always see selector; teachers only when they cover >1 room. */}
        {(isAdmin || classrooms.length > 1) && (
          <Field label="Classroom">
            <select
              data-testid="incident-classroom"
              value={form.classroomId}
              onChange={(e) =>
                setForm((f) => ({ ...f, classroomId: e.target.value }))
              }
              className={inputClass}
            >
              {classrooms.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>
        )}

        {/* Child */}
        <Field label="Child">
          <select
            data-testid="incident-child"
            value={form.childId}
            onChange={(e) => setForm((f) => ({ ...f, childId: e.target.value }))}
            className={inputClass}
            required
          >
            <option value="" disabled>Select a child…</option>
            {children.map((c) => (
              <option key={c.id} value={c.id}>
                {c.first_name} {c.last_name || ""}
              </option>
            ))}
          </select>
        </Field>

        {/* Incident type */}
        <Field label="Incident type">
          <div className="flex flex-wrap gap-2" data-testid="incident-type">
            {INCIDENT_TYPES.map((t) => {
              const meta = INCIDENT_TYPE_META[t];
              const Icon = meta.icon;
              const selected = t === form.incidentType;
              return (
                <button
                  key={t}
                  type="button"
                  data-testid={`incident-type-${t}`}
                  onClick={() => setForm((f) => ({ ...f, incidentType: t }))}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                    selected
                      ? "border-primary bg-primary/10 text-primary-hover"
                      : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                  }`}
                >
                  <Icon size={14} weight="duotone" aria-hidden />
                  {meta.label}
                </button>
              );
            })}
          </div>
        </Field>

        {/* Severity */}
        <Field label="Severity">
          <div className="flex gap-2" data-testid="incident-severity">
            {SEVERITIES.map((s) => {
              const selected = s === form.severity;
              const meta = SEVERITY_META[s];
              return (
                <button
                  key={s}
                  type="button"
                  data-testid={`incident-severity-${s}`}
                  onClick={() => setForm((f) => ({ ...f, severity: s }))}
                  className={`flex-1 rounded-full border px-4 py-2.5 text-sm font-semibold uppercase tracking-wider transition-all ${
                    selected
                      ? `${meta.ring} ${meta.bg} ${meta.color}`
                      : "border-neutral-200 bg-white text-neutral-500 hover:bg-neutral-50"
                  }`}
                >
                  {meta.label}
                </button>
              );
            })}
          </div>
        </Field>

        {/* Date + location */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Occurred at">
            <input
              data-testid="incident-occurred-at"
              type="datetime-local"
              value={form.occurredAt}
              onChange={(e) =>
                setForm((f) => ({ ...f, occurredAt: e.target.value }))
              }
              required
              className={inputClass}
            />
          </Field>
          <Field label="Location (optional)">
            <input
              data-testid="incident-location"
              type="text"
              value={form.location}
              onChange={(e) =>
                setForm((f) => ({ ...f, location: e.target.value }))
              }
              placeholder="e.g. Outdoor play area"
              className={inputClass}
            />
          </Field>
        </div>

        {/* Description */}
        <Field label="Description" error={descErr}>
          <textarea
            data-testid="incident-description"
            rows={4}
            value={form.description}
            onChange={(e) =>
              setForm((f) => ({ ...f, description: e.target.value }))
            }
            required
            placeholder="Describe what happened, where, and who was present."
            className={inputClass}
          />
        </Field>

        {/* Action taken */}
        <Field label="Action taken" error={actionErr}>
          <textarea
            data-testid="incident-action"
            rows={3}
            value={form.actionTaken}
            onChange={(e) =>
              setForm((f) => ({ ...f, actionTaken: e.target.value }))
            }
            required
            placeholder="Describe first aid or intervention provided."
            className={inputClass}
          />
        </Field>

        {/* Parent notification */}
        <div className="rounded-2xl border border-neutral-200 bg-white p-4">
          <label className="flex items-center gap-3">
            <input
              data-testid="incident-parent-notified"
              type="checkbox"
              checked={form.parentNotified}
              onChange={(e) =>
                setForm((f) => ({ ...f, parentNotified: e.target.checked }))
              }
              className="h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary/30"
            />
            <span className="text-sm font-medium text-neutral-900">
              Parent was notified in person or by phone
            </span>
          </label>
          {form.parentNotified && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="incident-notified-method">
              {NOTIFICATION_METHODS.map((m) => {
                const selected = m === form.parentNotificationMethod;
                return (
                  <button
                    key={m}
                    type="button"
                    data-testid={`incident-notified-method-${m}`}
                    onClick={() =>
                      setForm((f) => ({ ...f, parentNotificationMethod: m }))
                    }
                    className={`rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-all ${
                      selected
                        ? "border-primary bg-primary/10 text-primary-hover"
                        : "border-neutral-200 bg-white text-neutral-600"
                    }`}
                  >
                    {NOTIFICATION_METHOD_LABELS[m]}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Link
            to={basePath}
            data-testid="incident-cancel"
            className="rounded-full px-5 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-100"
          >
            Cancel
          </Link>
          <button
            type="submit"
            data-testid="incident-submit"
            disabled={!canSubmit || submitting}
            className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit report"}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputClass =
  "w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

function Field({ label, children, error }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </span>
      {children}
      {error && (
        <p className="mt-1 text-xs text-[#C6851E]">{error}</p>
      )}
    </label>
  );
}
